/**
 * Module-local lookup stores for tariff pickers.
 * - countries/currencies come from @botto/core (registered in module init from billing API)
 * - operators/tariffs stay local (tariff-domain only)
 * - aggregators come from SMS API via @botto/core/stores (cross-module)
 */
import {
  createLookupStore,
  getCountriesLookup,
  getCurrenciesLookup,
  type LookupStore,
} from '@botto/core';
import { tariffsApi } from '../api/client';
import type { Operator, Tariff } from '../api/types';

export { getCountriesLookup, getCurrenciesLookup };

let _operators: LookupStore<Operator> | undefined;
let _tariffs: LookupStore<Tariff> | undefined;

async function fetchAllOperators(): Promise<Operator[]> {
  const { data, error } = await tariffsApi.GET('/operators/', {
    params: { query: { offset: 0, limit: 1000, 'sort.name': 'operator', 'sort.direction': 'asc' } },
  });
  if (error) throw error;
  return data?.items ?? [];
}

export function getOperatorsLookup(): LookupStore<Operator> {
  if (!_operators) {
    _operators = createLookupStore<Operator>(fetchAllOperators, {
      idField: 'id',
      labelField: 'operator',
      format: (op) => `${op.operator ?? '—'} (#${op.id})`,
    });
  }
  return _operators;
}

async function fetchAllTariffs(): Promise<Tariff[]> {
  const { data, error } = await tariffsApi.GET('/tariffs/', {
    params: {
      query: {
        offset: 0,
        limit: 1000,
        'sort.name': 'name',
        'sort.direction': 'asc',
      },
    },
  });
  if (error) throw error;
  return data?.items ?? [];
}

export function getTariffsLookup(): LookupStore<Tariff> {
  if (!_tariffs) {
    _tariffs = createLookupStore<Tariff>(fetchAllTariffs, {
      idField: 'id',
      labelField: 'name',
      format: (t) => `${t.name ?? '—'} (#${t.id})`,
    });
  }
  return _tariffs;
}
